import { Button, DateInput, Icon, Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { ModalConfirmation } from '../../../../../components/modals/Confirmation';
import { SectionTitle } from '../../../../../components/text';
import Tooltip from '../../../../../components/tooltip/Tooltip';
import { useValidations } from '../../../../../hooks/useValidations';
import useFirstInstallmentRule from '../../hooks/useFirstInstallmentRule';
import { AdmissionScope, Offer } from '../../types';
import {
  getFirstInstallmentRule,
  getSelectedAdmissionScope,
} from '../../utils';

const PREFIX = 'offer_modal';

const DATE_INPUT_NAME = `${PREFIX}_selectDate`;

interface Props {
  offer?: Offer;
  availableOffers: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const DEFAULT_VALUES = {
  [`${PREFIX}_startDate`]: null,
  [`${PREFIX}_duration`]: null,
  [`${PREFIX}_enrollmentType`]: null,
  [`${PREFIX}_maxInstallments`]: null,
  [`${PREFIX}_quota`]: null,
  [`${PREFIX}_offerQuota`]: null,
  [`${PREFIX}_firstInstallmentRule`]: null,
  [`${PREFIX}_selectDay`]: null,
  [`${PREFIX}_selectMonth`]: null,
  [DATE_INPUT_NAME]: null,
  [`${PREFIX}_offerId`]: null,
};

export default function AdmissionProcessOfferForm({
  offer,
  availableOffers,
  onSubmit,
  onCancel,
  isSubmitting,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const { msgValidations } = useValidations();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const isEdit = !!offer;

  const texts = useMemo(() => {
    const prefix = 'offersList.modalForm';
    return {
      title: isEdit ? t(`${prefix}.title`) : t('offersTable.addOffer'),
      startDate: t(`${prefix}.startDate`),
      duration: t(`${prefix}.duration`),
      enrollmentType: t(`${prefix}.enrollmentType`),
      maxInstallments: t(`${prefix}.maxInstallments`),
      maxInstallmentsTooltip: t(`${prefix}.maxInstallmentsTooltip`),
      quota: t('offersTable.quota'),
      offerQuota: t(`${prefix}.offerQuota`),
      firstExpirationRule: t(`${prefix}.firstExpirationRule`),
      date: t(`${prefix}.date`),
      firstExpirationRuleTitle: t(`${prefix}.firstExpirationRuleTitle`),
      firstExpirationRuleSubTitle: t(`${prefix}.firstExpirationRuleSubTitle`),
      save: isEdit ? t(`${prefix}.save`) : t('common.add'),
      close: isEdit ? t(`${prefix}.close`) : t('common.cancel'),
      offer: t('offersTable.offer'),
    };
  }, [t, isEdit]);

  const { control, handleSubmit, reset, watch, setValue } = useForm<any>({
    defaultValues: DEFAULT_VALUES,
  });

  const firstInstallmentRule = watch(`${PREFIX}_firstInstallmentRule`);

  const { options, formFields } = useFirstInstallmentRule({
    key: firstInstallmentRule?.value as string,
    control,
    fieldPrefix: PREFIX,
  });

  useEffect(() => {
    if (isEdit && offer) {
      const commonValues = {
        [`${PREFIX}_startDate`]: offer?.startDate ?? null,
        [`${PREFIX}_duration`]: offer?.duration ?? null,
        [`${PREFIX}_enrollmentType`]: getSelectedAdmissionScope(
          offer?.admissionScopes ?? [],
          t,
        ),
        [`${PREFIX}_maxInstallments`]: offer?.maxInstallments
          ? {
              label: offer?.maxInstallments?.toString(),
              value: offer?.maxInstallments,
            }
          : null,
        [`${PREFIX}_quota`]: offer?.quota?.toString() ?? null,
        [`${PREFIX}_offerQuota`]: offer?.offerQuota?.toString() ?? null,
        [`${PREFIX}_offerId`]: {
          label: `${offer.studyPlanName} - ${offer.campusName} - ${offer.scheduleName}`,
          value: offer.admissionId,
        },
      };

      if (offer.firstInstallmentRule) {
        const {
          key,
          selectDate,
          selectMonth,
          selectYear,
          selectDay,
          validityRange,
        } = getFirstInstallmentRule(offer.firstInstallmentRule);

        const selectedOption = options.find((option) => option.value === key);

        reset({
          ...commonValues,
          [`${PREFIX}_firstInstallmentRule`]: {
            label: selectedOption?.label ?? '',
            value: selectedOption?.value ?? '',
          },
          [DATE_INPUT_NAME]: selectDate ?? null,
          [`${PREFIX}_selectMonth`]: selectMonth ?? null,
          [`${PREFIX}_selectYear`]: selectYear ?? null,
          [`${PREFIX}_selectDay`]: selectDay ?? null,
          [`${PREFIX}_validityRange`]: validityRange?.toString() ?? null,
        });
      } else {
        reset(commonValues);
      }
    }
  }, [isEdit, offer, reset, options, t]);

  const offerOptions = useMemo(() => {
    return availableOffers.map((o) => ({
      label: `${o.studyPlanName} - ${o.campusName} - ${o.scheduleName}`,
      value: o.id,
    }));
  }, [availableOffers]);

  const offerId = watch(`${PREFIX}_offerId`);

  useEffect(() => {
    if (offerId?.value) {
      const selectedOffer = availableOffers.find((o) => o.id === offerId.value);
      if (selectedOffer) {
        setValue(`${PREFIX}_startDate`, selectedOffer.startDate ?? null, {
          shouldValidate: true,
        });
        setValue(`${PREFIX}_duration`, selectedOffer.duration ?? null, {
          shouldValidate: true,
        });
        setValue(
          `${PREFIX}_offerQuota`,
          selectedOffer.offerQuota?.toString() ?? null,
          {
            shouldValidate: true,
          },
        );
      }
    }
  }, [offerId, availableOffers, setValue]);

  const handleFormSubmit = (data: any) => {
    const finalData = {
      ...data,
      offerId: data[`${PREFIX}_offerId`],
      quota: data[`${PREFIX}_quota`],
      offerQuota: data[`${PREFIX}_offerQuota`],
      startDate: data[`${PREFIX}_startDate`],
      duration: data[`${PREFIX}_duration`],
      enrollmentType: data[`${PREFIX}_enrollmentType`],
      maxInstallments: data[`${PREFIX}_maxInstallments`],
      firstInstallmentRule: data[`${PREFIX}_firstInstallmentRule`],
      selectDate: data[DATE_INPUT_NAME],
      selectDay: data[`${PREFIX}_selectDay`],
      selectMonth: data[`${PREFIX}_selectMonth`],
      selectYear: data[`${PREFIX}_selectYear`],
      validityRange: data[`${PREFIX}_validityRange`],
    };
    const quota = finalData.quota ? Number(finalData.quota) : 0;
    const offerQuota = finalData.offerQuota ? Number(finalData.offerQuota) : 0;

    if (quota > offerQuota) {
      setPendingData(finalData);
      setShowConfirmationModal(true);
      return;
    }
    onSubmit(finalData);
  };

  const handleConfirmSubmit = () => {
    if (pendingData) {
      onSubmit(pendingData);
    }
    setShowConfirmationModal(false);
    setPendingData(null);
  };

  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
    setPendingData(null);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Row>
        <Col xs={12}>
          <h3 className="fs-22 text-center mb-3">{texts.title}</h3>
        </Col>
        <Col xs={12} className="mt-3">
          <Select
            label={texts.offer}
            name={`${PREFIX}_offerId`}
            options={offerOptions}
            control={control}
            placeholder={t('common.select')}
            disabled={isEdit}
            rules={{ required: !isEdit ? msgValidations.required : false }}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <TextInput
            label={texts.quota}
            control={control}
            name={`${PREFIX}_quota`}
            rules={{
              pattern: {
                value: /^[0-9]*$/,
                message: msgValidations.invalidIntNumeric,
              },
            }}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <TextInput
            label={texts.offerQuota}
            control={control}
            name={`${PREFIX}_offerQuota`}
            readOnly={true}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <DateInput
            label={texts.startDate}
            control={control}
            name={`${PREFIX}_startDate`}
            disabled={true}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <TextInput
            label={texts.duration}
            control={control}
            name={`${PREFIX}_duration`}
            readOnly={true}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <Select
            label={texts.enrollmentType}
            control={control}
            name={`${PREFIX}_enrollmentType`}
            options={[
              {
                label: 'Matrícula presencial',
                value: AdmissionScope.OS,
              },
              {
                label: 'Matrícula autoatendida (online)',
                value: AdmissionScope.OL,
              },
              {
                label: 'Matrícula presencial y autoatendida',
                value: `${AdmissionScope.OS},${AdmissionScope.OL}`,
              },
            ]}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} md={6} className="mt-3">
          <Row xs={12}>
            <Col xs={11}>
              <Select
                label={texts.maxInstallments}
                control={control}
                name={`${PREFIX}_maxInstallments`}
                options={Array.from({ length: 12 }, (_, i) => ({
                  label: (i + 1).toString(),
                  value: (i + 1).toString(),
                }))}
                rules={{ required: msgValidations.required }}
              />
            </Col>
            <Col
              xs={1}
              className="d-flex align-items-center"
              style={{ marginLeft: '-14px' }}
            >
              <Tooltip
                id="info_tooltip_max_installments"
                placement="bottom"
                text={texts.maxInstallmentsTooltip}
              >
                <Icon name="information_input" className="mb-3" />
              </Tooltip>
            </Col>
          </Row>
        </Col>
        <Col xs={12} className="mt-3">
          <SectionTitle text={texts.firstExpirationRuleTitle} />
          <p className="fs-14 mb-3">{texts.firstExpirationRuleSubTitle}</p>
        </Col>
        <Col xs={12} className="mt-2">
          <Select
            label={texts.firstExpirationRule}
            name={`${PREFIX}_firstInstallmentRule`}
            options={options}
            control={control}
            placeholder={texts.firstExpirationRule}
            rules={{ required: false }}
          />
        </Col>
        {firstInstallmentRule && <Col xs={12}>{formFields}</Col>}
      </Row>
      <Row className="mt-5">
        <Col xs={6}>
          <Button
            text={texts.close}
            outlined
            onClick={onCancel}
            fullwidth
            disabled={isSubmitting}
          />
        </Col>
        <Col xs={6}>
          <Button
            text={texts.save}
            type="submit"
            fullwidth
            loading={isSubmitting}
          />
        </Col>
      </Row>
      <ModalConfirmation
        open={showConfirmationModal}
        title={''}
        subtitle={t('offersList.modalForm.quotaExceededMessage')}
        onClose={handleCancelSubmit}
        onConfirm={handleConfirmSubmit}
      />
    </form>
  );
}
